import { Fragment, ReactNode, useState } from "react"
import type { CSSProperties } from "react";
import Button from "./button"
import ReactModal from 'react-modal'
import { ELEMENT_MODAL_ID, Z_INDECES } from "@app/constants/default";
import Image from "next/image";
import { useThemeContext } from "@app/contexts/theme-context";
import Icon from "@mdi/react";
import { mdiCheckCircleOutline, mdiClock } from "@mdi/js";
import { Form } from "react-final-form";
import Input from "@app/components/form/input";
import PhoneInput from "@app/components/phone-input";
import Dropdown from "@app/components/form/dropdown";
import TextArea from "@app/components/text-area";
import Checkbox from "@app/components/checkbox";
import { toast } from "react-toastify";
import GradientContainer from "@app/components/gradient/gradient-container";

const CompanySizeOptions = [
    { name: '1–10 employees', value: 1 },
    { name: '11–50 employees', value: 2 },
    { name: '51–200 employees', value: 3 },
    { name: '201–1,000 employees', value: 4 },
    { name: '1,000+ employees', value: 5 },
];

const LocationCountOptions = [
    { name: '1 location', value: 1 },
    { name: '2–10 locations', value: 2 },
    { name: '11–50 locations', value: 3 },
    { name: '51–200 locations', value: 4 },
    { name: '200+ locations', value: 5 },
];

const DemoInterestOptions = [
    { name: 'Cost savings', value: 1 },
    { name: 'Centralized invoicing & reporting', value: 2 },
    { name: 'Sustainability / ESG reporting', value: 3 },
    { name: 'Hauler sourcing & procurement', value: 4 },
    { name: 'Other', value: 5 },
];

type DemoRequestFormValues = {
    firstName?: string;
    lastName?: string;
    businessEmail?: string;
    phone?: string;
    companyName?: string;
    jobTitle?: string;
    companySize?: string;
    locations?: string;
    interest?: string;
    message?: string;
};

export const DemoRequestForm = ({
    handleModalClose,
    onBack,
    showBack,
}: {
    handleModalClose: () => void;
    onBack?: () => void;
    showBack?: boolean;
}) => {
    const [hasConsented, setHasConsented] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);

    const handleSubmit = async (payload: DemoRequestFormValues, form) => {
        const selectedCompanySize = CompanySizeOptions.find((item) => item.value === Number(payload.companySize));
        const selectedLocations = LocationCountOptions.find((item) => item.value === Number(payload.locations));
        const selectedInterest = DemoInterestOptions.find((item) => item.value === Number(payload.interest));

        const requestPayload = {
            firstName: payload.firstName || '',
            lastName: payload.lastName || '',
            email: payload.businessEmail || '',
            phone: payload.phone || '',
            company: payload.companyName || '',
            jobTitle: payload.jobTitle || '',
            companySize: selectedCompanySize ? selectedCompanySize.name : '',
            locations: selectedLocations ? selectedLocations.name : '',
            interest: selectedInterest ? selectedInterest.name : '',
            message: payload.message || '',
            pageUrl: typeof window === 'undefined' ? '' : window.location.href,
        };

        try {
            const res = await fetch('/api/demo-request', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(requestPayload),
            });

            const data = await res.json().catch(() => ({}));
            if (!res.ok) {
                throw new Error(data?.message || 'Something went wrong');
            }

            toast.success(data?.message || 'Thanks! We’ll reach out to schedule your demo.', { position: "bottom-right" });
            form.reset();
            setIsSubmitted(true);
        } catch (err) {
            toast.error(err instanceof Error ? err.message : 'Something went wrong');
        }
    };

    if (isSubmitted) {
        return (
            <div className="p-6 sm:p-10">
                <div className="mx-auto max-w-md text-center">
                    <GradientContainer overrideClassName="mx-auto w-14 h-14 rounded-full p-[2px]">
                        <div className="w-full h-full rounded-full bg-white flex items-center justify-center">
                            <Icon path={mdiCheckCircleOutline} size={1.2} className="text-secondary" />
                        </div>
                    </GradientContainer>
                    <h4 className="mt-4">Request received</h4>
                    <p className="mt-2 text-sm text-text-color-mid-em">
                        Thanks — we’ll reach out to schedule your SimpleWaste platform demo.
                    </p>
                    <div className="mt-6 flex items-center justify-center">
                        <Button onClick={handleModalClose} className="px-12">
                            Close
                        </Button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="p-6 sm:p-8">
            <div className="space-y-2 mb-6">
                <h4>Request a demo</h4>
                <p className="text-sm text-text-color-mid-em">
                    Tell us a bit about your organization and we’ll reach out to schedule a time.
                </p>
            </div>
            <Form
                onSubmit={handleSubmit}
                render={({ handleSubmit, submitting }) => (
                    <form onSubmit={handleSubmit} className="space-y-5">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                            <Input label="First name*" name="firstName" required />
                            <Input label="Last name*" name="lastName" required />
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                            <Input label="Work email*" name="businessEmail" type="email" required />
                            <PhoneInput
                                initialValue="us"
                                name="phone"
                                label="Phone"
                                labelClassName="text-text-color-high-em label-md"
                                phoneInputClassName="text-text-color-mid-em focus:bg-white focus:ring-0 focus:border-secondary-light !w-full !h-12 !rounded-lg"
                            />
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                            <Input label="Company name*" name="companyName" required />
                            <Input label="Job title" name="jobTitle" />
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                            <Dropdown
                                name="companySize"
                                label="Company size"
                                selectId="demo_company_size"
                                options={CompanySizeOptions}
                                placeholder="Select…"
                            />
                            <Dropdown
                                name="locations"
                                label="Number of locations"
                                selectId="demo_locations"
                                options={LocationCountOptions}
                                placeholder="Select…"
                            />
                        </div>
                        <Dropdown
                            name="interest"
                            label="What are you looking for?"
                            selectId="demo_interest"
                            options={DemoInterestOptions}
                            placeholder="Select…"
                        />
                        <TextArea
                            name="message"
                            resizeable={false}
                            label="Additional details (optional)"
                            labelClassName="text-text-color-high-em label-md"
                            placeholder="Goals, current setup, timeline, preferred times, anything helpful…"
                            rows={4}
                        />
                        <Checkbox
                            checked={hasConsented}
                            onChange={(e) => setHasConsented(e.target.checked)}
                            description="You agree to our friendly privacy policy."
                        />
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-end gap-3">
                            {showBack && onBack && (
                                <Button buttonType="secondary" onClick={onBack}>
                                    Back
                                </Button>
                            )}
                            <Button buttonType="secondary" onClick={handleModalClose}>
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                disabled={!hasConsented || submitting}
                            >
                                {submitting ? 'Sending…' : 'Submit request'}
                            </Button>
                        </div>
                    </form>
                )}
            />
        </div>
    );
};



ReactModal.setAppElement(ELEMENT_MODAL_ID);

export interface ScheduleDemoProps {
    children: (setIsModalOpen) => ReactNode,
    skipIntro?: boolean,
}

const ScheduleDemo = ({ children, skipIntro = false }: ScheduleDemoProps) => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isIntro, setIsIntro] = useState(true && !skipIntro);
    const { isTablet, isMobile } = useThemeContext();

    const isTabletOrMobile = isTablet || isMobile;
    const modalWidthTabletMobile = isTablet ? '50vw' : '95vw';
    const modalWidth = isTabletOrMobile ? modalWidthTabletMobile : isIntro ? '420px' : '720px'

    const contentStyles: CSSProperties = {
        top: '50%',
        left: '50%',
        right: 'auto',
        bottom: 'auto',
        marginRight: '-50%',
        transform: 'translate(-50%, -50%)',
        borderRadius: '0.375rem',
        minWidth: modalWidth,
        maxWidth: modalWidth,
        maxHeight: '90vh',
        overflow: 'auto',
        zIndex: Z_INDECES.MODAL,
        background: 'white',
        border: '1px solid rgb(204, 204, 204)',
        boxShadow: '0 24px 48px rgba(16, 24, 40, 0.18)',
        padding: 0,
    };
    if (isIntro) contentStyles.minHeight = '30vh';

    const defaultModalStyles = {
        content: contentStyles,
        overlay: {
            zIndex: Z_INDECES.MODAL_OVERLAY,
            background: 'rgba(52,64,84,0.7)',
            ['--tw-backdrop-blur']: 'blur(12px)',
            backdropFilter: 'var(--tw-backdrop-blur) var(--tw-backdrop-brightness) var(--tw-backdrop-contrast) var(--tw-backdrop-grayscale) var(--tw-backdrop-hue-rotate) var(--tw-backdrop-invert) var(--tw-backdrop-opacity) var(--tw-backdrop-saturate) var(--tw-backdrop-sepia)'
        }
    };

    const handleModalClose = () => {
        setIsIntro(!skipIntro);
        setIsModalOpen(false);
    }

    const renderIntro = (
        <div className="p-6 sm:p-8 text-center">
            <div className="mb-5">
                <div className="mb-3">
                    <Image src="/assets/svgs/global/logo-mark.svg" width="160" height="30" alt="SimpleWaste Logo" />
                </div>
                <div id="schedule__demo" className="mb-3 rounded-lg">
                    <Image src="/images/schedule-demo-phone.png" alt="SimpleWaste Mobile" height={200} width={200} />
                </div>
                <div className="mb-3">
                    <h4 className="mb-1">SimpleWaste Platform Demo</h4>
                    <div className="flex justify-center">
                        <div className="flex items-center justify-center bg-green-100 rounded-xl text-secondary text-xs py-2 space-x-2 px-3">
                            <Icon path={mdiClock} size={0.8} />
                            <div>30 mins</div>
                        </div>
                    </div>
                </div>
                <p className="text-sm mb-5">
                    We look forward to discussing how we can meet your waste management needs.
                </p>
            </div>
            <div className="flex items-center space-x-3">
                <Button full buttonType="secondary" onClick={handleModalClose}>
                    Cancel
                </Button>
                <Button full onClick={(e) => {
                  e.stopPropagation()
                  setIsIntro(false)
                }}>
                    Proceed
                </Button>
            </div>
        </div>
    )

    return (
        <Fragment>
            <ReactModal
                isOpen={isModalOpen} contentLabel="Schedule Demo" style={defaultModalStyles}
                shouldCloseOnOverlayClick shouldCloseOnEsc onRequestClose={handleModalClose}
            >
                {isIntro ? renderIntro : (
                    <DemoRequestForm
                        handleModalClose={handleModalClose}
                        onBack={() => setIsIntro(true)}
                        showBack={!skipIntro}
                    />
                )}
            </ReactModal>
            {children(setIsModalOpen)}
        </Fragment>
    )
}

export default ScheduleDemo
